import React, { useState } from 'react';
import { Bot, Users, LogIn, Play, Copy, Lock, MapPin, Zap, Eye, Globe, BookOpen, ChevronLeft } from 'lucide-react';
import { TEAMS, TEAM_COLOR, cityBg } from './rtbBoard';

function CityPicker({ cities, era, city, onCity }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" role="radiogroup" aria-label="City">
      {cities.map((c) => {
        const locked = era < c.min_era;
        const on = city === c.id;
        return (
          <button key={c.id} type="button" data-testid={`rtb-city-${c.id}`} disabled={locked} onClick={() => onCity(c.id)} aria-pressed={on}
            className={`relative h-20 sm:h-24 rounded-lg overflow-hidden border text-left transition-[transform,border-color] duration-200 ${on ? 'scale-[1.03]' : 'hover:scale-[1.02]'} ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ borderColor: on ? c.accent : 'rgba(255,255,255,0.2)', boxShadow: on ? `0 0 18px ${c.accent}88` : 'none' }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${cityBg(c.id)}')` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute bottom-1.5 left-2 right-2">
              <div className="rtb-display text-lg leading-none text-white">{c.name}</div>
              <div data-testid={`rtb-city-perk-${c.id}`} className="text-[9px] tracking-wider text-[#F3DC8A] truncate">{locked ? `Unlocks in Era ${c.min_era}` : c.perk?.toUpperCase()}</div>
            </div>
            {locked && <Lock size={14} className="absolute top-2 right-2 text-white/80" />}
            {on && <MapPin size={14} className="absolute top-2 right-2" color={c.accent} />}
          </button>
        );
      })}
    </div>
  );
}

function TeamPicker({ team, onTeam }) {
  return (
    <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Your crew">
      {TEAMS.map((t) => {
        const on = team === t;
        const c = TEAM_COLOR[t];
        return (
          <button key={t} type="button" data-testid={`rtb-team-${t}`} aria-pressed={on} onClick={() => onTeam(t)}
            className={`relative rounded-xl border-2 px-2 py-2 flex flex-col sm:flex-row items-center gap-2 text-left bg-black/60 transition-[transform,box-shadow] duration-200 ${on ? 'scale-[1.04]' : 'opacity-75 hover:opacity-100'}`}
            style={{ borderColor: on ? c : `${c}55`, boxShadow: on ? `0 0 20px ${c}88` : 'none' }}>
            <img src={`/rtb/face_${t}.png`} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover object-top" style={{ background: `${c}44` }} />
            <div className="min-w-0 text-center sm:text-left">
              <div className="rtb-display text-base sm:text-xl leading-none" style={{ color: c }}>{t.toUpperCase()}</div>
              <div className="text-[9px] sm:text-[10px] text-white/55">4 operatives</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function OnlinePanel({ busy, onOpen, onJoin, onBack }) {
  const [code, setCode] = useState('');
  return (
    <div data-testid="rtb-online-panel" className="rounded-xl border border-white/15 bg-black/60 p-4 space-y-3">
      <button type="button" onClick={onBack} data-testid="rtb-online-back" className="text-xs text-white/60 flex items-center gap-1 hover:text-white"><ChevronLeft size={14} /> back</button>
      <button type="button" data-testid="rtb-open-table-btn" disabled={busy} onClick={onOpen} className="rtb-btn-gold w-full h-12 rounded-xl flex items-center justify-center gap-2 rtb-display text-lg tracking-wider"><Users size={18} /> OPEN A TABLE</button>
      <div className="flex gap-2">
        <input data-testid="rtb-join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={5} placeholder="TABLE CODE"
          className="flex-1 min-w-0 h-12 rounded-lg bg-white/5 border border-white/20 px-4 text-white tracking-[0.3em] placeholder:text-white/30 outline-none focus:border-[#C9A84C]" />
        <button type="button" data-testid="rtb-join-btn" disabled={busy || code.length !== 5} onClick={() => onJoin(code)} className="rtb-btn-ghost h-12 px-5 rounded-lg flex items-center gap-2 rtb-display tracking-wider disabled:opacity-40"><LogIn size={18} /> JOIN</button>
      </div>
    </div>
  );
}

export function RtbLobby({ cities, era, city, onCity, wagers, busy, error, onCreate, onJoin, onRules }) {
  const [wager, setWager] = useState(wagers[1] || 5000);
  const [team, setTeam] = useState('green');
  const [count, setCount] = useState(4);
  const [online, setOnline] = useState(false);
  const opts = (mode) => ({ wager, mode, city, team, teams: count });
  return (
    <div data-testid="rtb-lobby" className="relative z-10 max-w-4xl px-4 sm:px-8 py-8 sm:py-10 space-y-5">
      <div>
        <span className="inline-block rounded-full border border-[#EF4444]/60 px-3 py-1 text-[10px] tracking-[0.3em] text-[#FCA5A5]">KILLING OVER WATER UNIVERSE · 16 OPERATIVES</span>
        <div className="leading-none mt-3"><span className="rtb-display text-5xl sm:text-7xl text-white">RUN THE </span><span className="rtb-marker text-5xl sm:text-7xl text-[#EF4444]" style={{ textShadow: '0 0 24px #EF444488' }}>BLOCK</span></div>
        <p className="mt-3 max-w-xl text-sm sm:text-base text-white/75">Pop the dice dome, push your crew around the block, send rivals back to base, trigger 3-strike combat, and bring all four operatives HOME first.</p>
      </div>
      <CityPicker cities={cities} era={era} city={city} onCity={onCity} />
      {cities.find((c) => c.id === city) && (
        <p data-testid="rtb-lobby-perk-desc" className="-mt-2 text-xs text-white/70"><span className="text-[#F3DC8A]">{cities.find((c) => c.id === city).perk}:</span> {cities.find((c) => c.id === city).perk_desc}</p>
      )}
      <TeamPicker team={team} onTeam={setTeam} />
      <div className="flex flex-wrap items-center gap-2">
        {wagers.map((v) => (
          <button key={v} type="button" data-testid={`rtb-wager-${v}`} onClick={() => setWager(v)} aria-pressed={wager === v}
            className={`h-10 px-4 rounded-full border rtb-display text-lg tracking-wider transition-colors ${wager === v ? 'border-[#C9A84C] bg-[#C9A84C] text-black' : 'border-white/25 text-white/80 hover:border-[#C9A84C]'}`}>
            ${v.toLocaleString()}
          </button>
        ))}
        <span className="text-xs text-white/50">buy-in · winner takes 4x</span>
      </div>
      {online ? (
        <OnlinePanel busy={busy} onOpen={() => onCreate(opts('lobby'))} onJoin={onJoin} onBack={() => setOnline(false)} />
      ) : (
        <div className="space-y-3 max-w-2xl">
          <button type="button" data-testid="rtb-play-ai-btn" disabled={busy} onClick={() => onCreate(opts('ai'))}
            className="rtb-cta w-full h-14 sm:h-16 rounded-2xl flex items-center justify-center gap-3 rtb-display text-xl sm:text-2xl tracking-wider text-white" style={{ background: 'linear-gradient(90deg,#DC2626,#F59E0B)' }}>
            <Zap size={22} /> QUICK SOLO MATCH (YOU + {count - 1} AI)
          </button>
          <button type="button" data-testid="rtb-spectate-btn" disabled={busy} onClick={() => onCreate({ ...opts('spectate'), teams: 4 })}
            className="rtb-cta w-full h-14 sm:h-16 rounded-2xl flex items-center justify-center gap-3 rtb-display text-xl sm:text-2xl tracking-wider text-white" style={{ background: 'linear-gradient(90deg,#0EA5E9,#4F46E5)' }}>
            <Bot size={22} /> AI BATTLE PREVIEW / SPECTATE (4 AI)
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="rtb-online-btn" onClick={() => setOnline(true)} className="rtb-btn-ghost h-12 sm:h-14 rounded-xl gap-2 rtb-display text-lg tracking-wider"><Globe size={18} /> ONLINE LOBBY</button>
            <button type="button" data-testid="rtb-lobby-rules-btn" onClick={onRules} className="rtb-btn-ghost h-12 sm:h-14 rounded-xl gap-2 rtb-display text-lg tracking-wider"><BookOpen size={18} /> HOW TO PLAY</button>
          </div>
        </div>
      )}
      <div className="max-w-2xl flex items-center justify-between rounded-xl border border-white/15 bg-black/60 px-4 py-2.5">
        <span className="rtb-display tracking-[0.2em] text-white/85">ACTIVE TEAMS</span>
        <div className="flex gap-2" role="radiogroup" aria-label="Active teams">
          {[2, 3, 4].map((n) => (
            <button key={n} type="button" data-testid={`rtb-teams-${n}`} aria-pressed={count === n} onClick={() => setCount(n)}
              className={`h-9 px-3 sm:px-4 rounded-lg text-sm font-semibold transition-colors ${count === n ? 'bg-[#EF4444] text-white' : 'bg-white/10 text-white/70 hover:bg-white/15'}`}>{n} Teams</button>
          ))}
        </div>
      </div>
      {error && <p data-testid="rtb-error" className="text-sm text-[#F87171]">{error}</p>}
      <p className="text-[11px] text-white/40 flex items-center gap-1.5"><Eye size={12} /> Spectate costs nothing. Block cash and heat from real matches carry into your game.</p>
    </div>
  );
}

export function RtbWaitingRoom({ cityName, match, myTeam, busy, onStart, onLeave }) {
  const host = match.seats[myTeam] && match.host === match.seats[myTeam].game_id;
  const seats = match.active || TEAMS;
  return (
    <div data-testid="rtb-waiting-room" className="relative z-10 mx-auto max-w-xl px-4 py-12 text-center">
      <p className="text-[10px] tracking-[0.35em] text-white/50">OPEN TABLE{cityName ? ` · ${cityName.toUpperCase()}` : ''}</p>
      <h3 className="rtb-display text-6xl text-white tracking-[0.2em] mt-2" data-testid="rtb-table-code">{match.code}</h3>
      <button type="button" onClick={() => navigator.clipboard?.writeText(match.code)} data-testid="rtb-copy-code" className="mt-1 text-xs text-[#C9A84C] inline-flex items-center gap-1"><Copy size={12} /> copy code</button>
      <p className="text-sm text-white/60 mt-3">Share this code. Other players join from their Run The Block screen. Empty seats become AI crews when the host starts.</p>
      <div className="mt-6 grid grid-cols-2 gap-2">
        {seats.map((t) => (
          <div key={t} data-testid={`rtb-seat-${t}`} className="rounded-lg border px-3 py-3 text-left" style={{ borderColor: TEAM_COLOR[t], background: `${TEAM_COLOR[t]}1a` }}>
            <div className="rtb-display text-lg" style={{ color: TEAM_COLOR[t] }}>{t.toUpperCase()}</div>
            <div className="text-xs text-white/70">{match.seats[t].kind === 'open' ? 'Waiting…' : match.seats[t].name}{t === myTeam ? ' (you)' : ''}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-center gap-3">
        {host ? <button type="button" data-testid="rtb-start-btn" disabled={busy} onClick={onStart} className="rtb-btn-gold h-12 px-6 rounded-full flex items-center gap-2 rtb-display tracking-wider"><Play size={18} /> START MATCH</button>
          : <p className="text-sm text-white/60">Waiting for the host to start…</p>}
        <button type="button" data-testid="rtb-waiting-leave" onClick={onLeave} className="rtb-btn-ghost h-12 px-5 rounded-full">Leave</button>
      </div>
    </div>
  );
}
